/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { Form, Input, Typography, Button, message, Alert } from 'antd';
import { useMutation, queryCache } from 'react-query';
import { useHistory } from 'react-router-dom';
import {
  validateUserManagementToken,
  UserManagementLanguage,
} from '~src/services/api/userManagment';
import { isEmailValid } from '~src/utils/login';
import {
  createUserCreationToken,
  registerUserWithPrivateKey,
} from '~src/services/api/user';
import Project from '~src/assets/nav-icons/project.png';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import CachedSharesContext from '~src/contexts/CachedSharesContext';

// Імпорти для логіну
import { apiCweb, getCookieName, loginReq } from '~src/services/api/api-cweb';
import { setAuthCookie } from '~src/hooks/utils/authCookie';

const { Text, Link } = Typography;

type Step = 1 | 2 | 3 | 4;

const SIMPLE_KEY_MAX_LENGTH = 10;
const RESEND_COOLDOWN_SECONDS = 60;

// Update Interface to allow nulls as per Swagger
export interface UserCrVM {
  email: string;
  password: string;
  full_name: string | null;
  company_name: string | null;
  address: string | null;
  zipcode: string | null;
  town: string | null;
  phone_land_code: string | null;
  phone_number: string | null;
  mobile_land_code: string | null;
  mobile_number: string | null;
}

export type CreateUserProps = {
  shareId?: string | undefined;
  shareKey?: string | undefined | null;
  switchTab?: (tab: string) => void;
  onReset?: () => void;
};

// --- Helpers ---

const getUserManagementLanguage = (locale: string): UserManagementLanguage => {
  if (locale.startsWith('da')) return 'da';
  if (locale.startsWith('de')) return 'de';
  if (locale.startsWith('sv')) return 'sv';
  return 'en';
};

const sanitizeSimpleKey = (val: string): string =>
  val
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, SIMPLE_KEY_MAX_LENGTH);

const formatSimpleKeyForDisplay = (raw: string): string => {
  const cleaned = sanitizeSimpleKey(raw);
  const p1 = cleaned.slice(0, 4);
  const p2 = cleaned.slice(4, 6);
  const p3 = cleaned.slice(6, 10);
  const parts = [p1, p2, p3].filter(Boolean);
  return parts.join('-');
};

const isPasswordStrong = (password: string): boolean => {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /\d/.test(password)
  );
};

export const CreateUserClaim: React.FC<CreateUserProps> = ({
  shareId,
  shareKey,
  switchTab,
  onReset,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const [form] = Form.useForm();

  const history = useHistory();
  const { cachedSharesContext, setCachedSharesContext } = useContext(
    CachedSharesContext
  );

  const [step, setStep] = useState<Step>(1);
  const [cooldown, setCooldown] = useState(0);

  // State
  const [email, setEmail] = useState('');
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [privateKey, setPrivateKey] = useState<string | null>(null);

  // Verification Code State
  const [verificationCodeRaw, setVerificationCodeRaw] = useState('');

  // Password State
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Error State for Step 4
  const [creationError, setCreationError] = useState<string | null>(null);

  const userManagementLanguage = useMemo(
    () => getUserManagementLanguage(intl.locale || 'en'),
    [intl.locale]
  );

  // Cooldown Timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cooldown]);

  // --- Logic: Restart Session (Keep Form Data) ---
  const handleRestartSession = () => {
    // 1. Reset Auth State
    setPublicKey(null);
    setPrivateKey(null);
    setVerificationCodeRaw('');
    setCooldown(0);
    setCreationError(null);

    // 2. Go to Step 1
    setStep(1);

    // Note: We intentionally DO NOT reset 'form', 'email', 'password', etc.
    // This preserves the user's input so they only have to do the code verification again.
  };

  // --- API Mutations ---

  // 1. Create Token (Send Email)
  const [doCreateToken, createTokenMutation] = useMutation(
    createUserCreationToken,
    {
      onSuccess: (data) => {
        const rawPublicKey = (data.public_key || '').toString();
        setPublicKey(rawPublicKey);

        // Move to Step 2
        setStep(2);
        setCooldown(RESEND_COOLDOWN_SECONDS);

        message.success(
          intl.formatMessage({
            id: 'tender-claim-create-user-email-sent',
          })
        );
      },
      onError: (error: any) => {
        const status = error?.response?.status;

        if (status === 409) {
          form.setFields([
            {
              name: 'email',
              errors: [
                intl.formatMessage({
                  id: 'cached-share-action-create-user-email-exist',
                  defaultMessage: 'The email address is already in use',
                }),
              ],
            },
          ]);
        } else {
          // Default generic error toast for other issues
          message.error(
            intl.formatMessage({
              id: 'tender-claim-create-user-email-error',
            })
          );
        }
      },
    }
  );

  // 2. Validate Token (Get Private Key)
  const [doValidateToken, validateTokenMutation] = useMutation(
    validateUserManagementToken,
    {
      onSuccess: (data) => {
        setPrivateKey(data.private_key);
        // Move to Step 3
        setStep(3);

        message.success(
          intl.formatMessage({
            id: 'tender-claim-create-user-code-success',
          })
        );
      },
      onError: () => {
        message.error(
          intl.formatMessage({
            id: 'tender-claim-create-user-code-error',
          })
        );
      },
    }
  );

  // --- auto-login + redirect to claimed tender after successful registration ---
  const [doAutoLogin, loginMutation] = useMutation(loginReq, {
    onSuccess: (token) => {
      // 1. Authorization (token & header)
      setAuthCookie(getCookieName() as string, token);
      apiCweb.defaults.headers.common.Authorization = `Bearer ${token}`;

      // 2. clear cache
      queryCache.clear();
      queryCache.invalidateQueries('current-user');

      // 3. invalidate shares
      queryCache.invalidateQueries('shares');
      queryCache.invalidateQueries([QueryKeys.sharedTenderDetails]);

      // 4. Clear claimed share from cached shares list to sync /invitations
      if (shareId && shareKey) {
        const updatedCachedShares = cachedSharesContext.filter(
          (share) => !(share.shareId === shareId && share.shareKey === shareKey)
        );

        setCachedSharesContext(updatedCachedShares);
        localStorage.setItem('shares', JSON.stringify(updatedCachedShares));

        // 5. Redirect to claimed tender
        history.push(`/my-tenders/${shareId}-${shareKey}`);
      } else {
        history.push('/my-tenders');
      }

      onReset?.();
    },
    onError: () => {
      // if login fails we go to user login
      message.error(
        intl.formatMessage({
          id: 'tender-claim-login-error',
          defaultMessage: 'Automatic login failed. Please log in manually.',
        })
      );
      switchTab?.('user-login');
    },
  });

  // 3. Final Registration (Create User)
  const [doRegisterUser, registerUserMutation] = useMutation(
    registerUserWithPrivateKey,
    {
      onSuccess: () => {
        message.success(
          intl.formatMessage({
            id: 'tender-claim-create-user-success',
          })
        );

        // Щойно користувач створився — одразу робимо автоматичний логін
        doAutoLogin({
          username: email,
          password: password,
        });
      },
      onError: (error: any) => {
        const msg =
          error?.response?.data?.message ||
          intl.formatMessage({
            id: 'tender-claim-create-user-failed',
          });
        setCreationError(msg);
      },
    }
  );

  // --- Handlers ---

  // Step 1: Send Email
  const handleStep1 = async () => {
    try {
      await form.validateFields(['email']);
      const emailVal = form.getFieldValue('email');

      if (!isEmailValid(emailVal)) {
        message.error(
          intl.formatMessage({
            id: 'tender-claim-create-user-invalid-email',
          })
        );
        return;
      }

      setEmail(emailVal);

      doCreateToken({
        shareId: String(shareId),
        shareKey: String(shareKey),
        email: emailVal,
        language: userManagementLanguage,
      });
    } catch (e) {
      // Validation error
    }
  };

  // Step 2: Verify Code
  const handleStep2 = () => {
    if (!publicKey) {
      message.error(
        intl.formatMessage({
          id: 'tender-claim-create-user-session-invalid-start-over',
        })
      );
      handleRestartSession();
      return;
    }

    const simpleKey = sanitizeSimpleKey(verificationCodeRaw);
    if (!simpleKey) {
      message.error(
        intl.formatMessage({
          id: 'tender-claim-create-user-code-required',
        })
      );
      return;
    }

    doValidateToken({
      publicKey,
      simpleKey,
    });
  };

  const handleResend = () => {
    if (cooldown > 0) return;
    doCreateToken({
      shareId: String(shareId),
      shareKey: String(shareKey),
      email: email,
      language: userManagementLanguage,
    });
  };

  // Step 3: Password Check
  const handleStep3 = () => {
    // Manual validation for password match and strength
    if (!isPasswordStrong(password)) return;
    if (password !== confirmPassword) return;

    setStep(4);
  };

  // Step 4: Submit Full Form
  const handleStep4 = async (values: any) => {
    if (!privateKey || !publicKey) {
      message.error(
        intl.formatMessage({
          id: 'tender-claim-create-user-session-invalid-restart-verification',
        })
      );
      setCreationError(
        intl.formatMessage({
          id: 'tender-claim-create-user-session-invalid-restart-verification',
        })
      );
      return;
    }

    // Convert empty strings to NULL.
    const payload: UserCrVM = {
      email: email,
      password: password,
      full_name: values.full_name || null,
      company_name: values.company_name || null,
      address: values.address || null,
      zipcode: values.zipcode || null,
      town: values.town || null,
      phone_number: values.phone_number || null,
      mobile_number: values.mobile_number || null,
      phone_land_code: null,
      mobile_land_code: null,
    };

    doRegisterUser({
      publicKey,
      privateKey,
      data: payload,
    });
  };

  // Derived state for errors
  const passwordWeak =
    step === 3 && password.length > 0 && !isPasswordStrong(password);
  const passwordMismatch =
    step === 3 && confirmPassword.length > 0 && password !== confirmPassword;

  const normalizedVerificationCode = sanitizeSimpleKey(verificationCodeRaw);

  // check if registration or login is in progress
  const isSubmitting =
    registerUserMutation.isLoading || loginMutation.isLoading;

  return (
    <div css={styles.formContainer}>
      <div css={{ textAlign: 'center', marginBottom: 16 }}>
        <img
          src={Project}
          alt=""
          css={{ width: '20px', marginRight: '10px' }}
        />
        {intl.formatMessage({
          id: 'tender-claim-create-user-title',
        })}
      </div>

      <Form
        form={form}
        layout="vertical"
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
        onFinish={handleStep4}>
        {/* ---------------- STEP 1: Email ---------------- */}
        <div style={{ display: step === 1 ? 'block' : 'none' }}>
          <Form.Item
            name="email"
            label={intl.formatMessage({
              id: 'tender-claim-create-user-email-label',
            })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'tender-claim-create-user-email-required',
                }),
              },
              {
                type: 'email',
                message: intl.formatMessage({
                  id: 'tender-claim-create-user-email-invalid',
                }),
              },
            ]}>
            <Input
              css={styles.input}
              placeholder={intl.formatMessage({
                id: 'tender-claim-create-user-email-placeholder',
              })}
              autoComplete="email"
              allowClear
              disabled={step > 1} // Keep consistent with flow, usually unlocked if we allow 'back'
            />
          </Form.Item>

          <div css={styles.footerStack}>
            <Button
              css={styles.button}
              type="primary"
              loading={createTokenMutation.isLoading}
              onClick={handleStep1}>
              {intl.formatMessage({
                id: 'tender-claim-create-user-send-code',
              })}
            </Button>

            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12, marginRight: 4 }}>
                {intl.formatMessage({
                  id: 'tender-claim-create-user-already-have-account',
                })}
              </Text>
              <Link onClick={() => switchTab?.('user-login')}>
                {intl.formatMessage({
                  id: 'tender-claim-create-user-login-link',
                })}
              </Link>
            </div>
          </div>
        </div>

        {/* ---------------- STEP 2: Verification Code ---------------- */}
        <div style={{ display: step === 2 ? 'block' : 'none' }}>
          <p css={styles.label}>
            {intl.formatMessage({
              id: 'tender-claim-create-user-verification-code',
            })}
            :
          </p>

          <div css={styles.codeRow}>
            <Input
              css={styles.inputMono}
              placeholder={intl.formatMessage({
                id: 'tender-claim-create-user-verification-code-placeholder',
              })}
              value={formatSimpleKeyForDisplay(verificationCodeRaw)}
              maxLength={20}
              onChange={({ target: { value } }) =>
                setVerificationCodeRaw(value)
              }
            />

            <Button
              css={styles.resendButton}
              type="link"
              htmlType="button"
              loading={createTokenMutation.isLoading}
              disabled={cooldown > 0}
              onClick={handleResend}>
              {cooldown > 0
                ? intl.formatMessage(
                    {
                      id: 'tender-claim-create-user-resend-wait',
                    },
                    { seconds: cooldown }
                  )
                : intl.formatMessage({
                    id: 'tender-claim-create-user-resend-code',
                  })}
            </Button>
          </div>

          <div css={styles.footerBetween}>
            <Button onClick={() => setStep(1)}>
              {intl.formatMessage({
                id: 'tender-claim-create-user-go-back',
              })}
            </Button>
            <Button
              type="primary"
              loading={validateTokenMutation.isLoading}
              onClick={handleStep2}
              disabled={!normalizedVerificationCode}>
              {intl.formatMessage({
                id: 'tender-claim-create-user-verify-code',
              })}
            </Button>
          </div>
        </div>

        {/* ---------------- STEP 3: Password ---------------- */}
        <div style={{ display: step === 3 ? 'block' : 'none' }}>
          <p css={styles.label}>
            {intl.formatMessage({
              id: 'tender-claim-create-user-password-label',
            })}
            :
          </p>
          <Input.Password
            css={(passwordWeak || passwordMismatch) && styles.inputInvalid}
            placeholder={intl.formatMessage({
              id: 'tender-claim-create-user-password-placeholder',
            })}
            value={password}
            onChange={({ target: { value } }) => setPassword(value)}
            autoComplete="new-password"
          />

          {passwordWeak && (
            <div css={styles.fieldError}>
              <span>
                {intl.formatMessage({
                  id: 'tender-claim-create-user-password-requirements',
                })}
              </span>
            </div>
          )}

          <p css={styles.label}>
            {intl.formatMessage({
              id: 'tender-claim-create-user-confirm-password-label',
            })}
            :
          </p>
          <Input.Password
            css={(passwordWeak || passwordMismatch) && styles.inputInvalid}
            placeholder={intl.formatMessage({
              id: 'tender-claim-create-user-confirm-password-placeholder',
            })}
            value={confirmPassword}
            onChange={({ target: { value } }) => setConfirmPassword(value)}
          />

          {passwordMismatch && (
            <div css={styles.fieldError}>
              <span>
                {intl.formatMessage({
                  id: 'tender-claim-create-user-password-mismatch',
                })}
              </span>
            </div>
          )}

          <div css={styles.footerBetween}>
            <div />
            <Button
              type="primary"
              onClick={handleStep3}
              disabled={
                passwordWeak ||
                passwordMismatch ||
                !password ||
                !confirmPassword
              }>
              {intl.formatMessage({
                id: 'tender-claim-create-user-next',
              })}
            </Button>
          </div>
        </div>

        {/* ---------------- STEP 4: Profile Details ---------------- */}
        <div style={{ display: step === 4 ? 'block' : 'none' }}>
          <Form.Item
            name="full_name"
            label={intl.formatMessage({
              id: 'tender-claim-create-user-full-name-label',
            })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'tender-claim-create-user-full-name-required',
                }),
              },
            ]}>
            <Input css={styles.input} autoComplete="name" allowClear />
          </Form.Item>

          <Form.Item
            name="company_name"
            label={intl.formatMessage({
              id: 'tender-claim-create-user-company-name-label',
            })}>
            <Input css={styles.input} autoComplete="organization" allowClear />
          </Form.Item>

          <Form.Item
            name="address"
            label={intl.formatMessage({
              id: 'tender-claim-create-user-address-label',
            })}>
            <Input
              css={styles.input}
              autoComplete="street-address"
              allowClear
            />
          </Form.Item>

          <div css={styles.row2}>
            <Form.Item
              name="zipcode"
              label={intl.formatMessage({
                id: 'tender-claim-create-user-zip-code-label',
              })}
              rules={[
                {
                  pattern: /^\d+$/,
                  message: intl.formatMessage({
                    id: 'tender-claim-create-user-digits-only',
                  }),
                },
              ]}>
              <Input css={styles.input} autoComplete="postal-code" allowClear />
            </Form.Item>

            <Form.Item
              name="town"
              label={intl.formatMessage({
                id: 'tender-claim-create-user-town-label',
              })}>
              <Input
                css={styles.input}
                autoComplete="address-level2"
                allowClear
              />
            </Form.Item>
          </div>

          <Form.Item
            name="phone_number"
            label={intl.formatMessage({
              id: 'tender-claim-create-user-phone-number-label',
            })}
            rules={[
              {
                pattern: /^\d+$/,
                message: intl.formatMessage({
                  id: 'tender-claim-create-user-digits-only',
                }),
              },
            ]}>
            <Input css={styles.input} autoComplete="tel" allowClear />
          </Form.Item>

          <Form.Item
            name="mobile_number"
            label={intl.formatMessage({
              id: 'tender-claim-create-user-mobile-number-label',
            })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'tender-claim-create-user-mobile-number-required',
                }),
              },
              {
                pattern: /^\d+$/,
                message: intl.formatMessage({
                  id: 'tender-claim-create-user-digits-only',
                }),
              },
            ]}>
            <Input css={styles.input} autoComplete="tel-national" allowClear />
          </Form.Item>

          {/* Feature 1: Error display & Restart logic */}
          {creationError && (
            <div style={{ marginBottom: 16 }}>
              <Alert
                message={intl.formatMessage({
                  id: 'tender-claim-create-user-error-title',
                })}
                description={creationError}
                type="error"
                showIcon
                action={
                  <Button
                    size="small"
                    danger
                    onClick={handleRestartSession}
                    style={{ marginTop: 8 }}>
                    {intl.formatMessage({
                      id: 'tender-claim-create-user-restart-verification',
                    })}
                  </Button>
                }
              />
            </div>
          )}

          <div css={styles.footerBetween}>
            <Button onClick={() => setStep(3)} disabled={isSubmitting}>
              {intl.formatMessage({
                id: 'tender-claim-create-user-go-back',
              })}
            </Button>
            <Button type="primary" htmlType="submit" loading={isSubmitting}>
              {intl.formatMessage({
                id: 'tender-claim-create-user-submit',
              })}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  formContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    minHeight: 260,
  },
  input: {},
  inputMono: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
    letterSpacing: 2,
    textAlign: 'center',
  },
  row2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  footerStack: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    marginTop: 24,
  },
  footerBetween: {
    marginTop: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  button: {
    textTransform: 'uppercase',
    backgroundColor: theme.colors.orange,
    border: 'none',
    color: theme.colors.white,
    ':hover': {
      backgroundColor: theme.colors.darkOrange,
      color: theme.colors.white,
    },
    ':focus': {
      backgroundColor: theme.colors.darkOrange,
      color: theme.colors.white,
    },
  },
  codeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  resendButton: {
    marginTop: 0,
    padding: 0,
  },
  label: {
    marginTop: '20px',
  },
  inputInvalid: {
    border: `2px solid ${theme.colors.red}`,
  },
  fieldError: {
    marginTop: '6px',
    color: theme.colors.red,
    fontSize: '13px',
    lineHeight: '1.5',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    width: '100%',
    boxSizing: 'border-box',
  },
});
